/**
 * ForgotPasswordPage — fordert eine Passwort-Zurücksetzung an.
 *
 * Ohne diese Seite hätte ein ausgesperrter WorkspaceAdmin keinen Weg zurück: Support-Zugriff auf
 * Kunden-Workspaces ist bewusst nicht gebaut, und ein zweiter Administrator ist nicht garantiert.
 *
 * Der Server antwortet für jede Adresse und jedes Kürzel gleich mit Erfolg – kein
 * Enumerations-Orakel. „Schau in dein Postfach“ steht deshalb nur nach dieser Antwort; ein Fehler
 * oder keine Antwort heißt: offen, ob ein Link unterwegs ist.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { toApiError, useApi } from '@/shared/api';
import { Button, FormField, Input } from '@/shared/components';
import { AuthAlert } from './AuthAlert';
import type { AuthFailure } from './loginFailure';
import { AuthPageHeader } from './AuthPageHeader';
import { AuthStatusPage } from './AuthStatusPage';
import { authBackLinkClass } from './authLinkStyles';

const schema = z.object({
  tenantSlug: z
    .string()
    .min(1, 'Gib dein Unternehmenskürzel ein.')
    .regex(/^[a-z0-9-]+$/, 'Nur Kleinbuchstaben, Ziffern und Bindestriche.'),
  email: z.string().email('Gib eine gültige E-Mail-Adresse ein.'),
});

type Values = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const api = useApi();
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<AuthFailure | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { tenantSlug: '', email: '' },
  });

  const onSubmit = async (values: Values) => {
    setFailure(null);
    try {
      await api.auth.forgotPassword(values);
      setSent(true);
    } catch (error) {
      const { status } = await toApiError(error);
      setFailure(
        status === 429
          ? {
              title: 'Zu viele Anfragen.',
              text: 'Warte einen Moment und versuch es dann noch einmal.',
            }
          : {
              title: [0, 502, 503, 504].includes(status)
                ? 'Keine Antwort vom Server.'
                : `Der Server meldet einen Fehler (Status ${status}).`,
              text: 'Offen, ob ein Link unterwegs ist. Schau in dein Postfach oder versuch es gleich noch einmal.',
            },
      );
    }
  };

  if (sent)
    return (
      <AuthStatusPage
        eyebrow="Passwort"
        title="Schau in dein Postfach"
        text="Falls es zu diesen Angaben ein Konto gibt, haben wir dir einen Link zum Zurücksetzen geschickt. Der Link gilt nur kurze Zeit."
        main={{ label: 'Zurück zur Anmeldung', to: '/login' }}
      />
    );

  return (
    <>
      <AuthPageHeader
        eyebrow="Passwort"
        title="Passwort zurücksetzen"
        description="Wir schicken dir einen Link an deine hinterlegte E-Mail-Adresse."
      />
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-[18px]" noValidate>
        {failure && <AuthAlert title={failure.title} text={failure.text} />}
        <FormField
          label="Unternehmenskürzel"
          error={errors.tenantSlug?.message}
          hint="Dasselbe Kürzel wie bei der Anmeldung."
          required
        >
          <Input
            {...register('tenantSlug')}
            autoComplete="organization"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            className="font-mono"
          />
        </FormField>
        <FormField label="E-Mail" error={errors.email?.message} required>
          <Input {...register('email')} type="email" autoComplete="email" />
        </FormField>
        <Button
          type="submit"
          variant="brand"
          size="lg"
          className="h-[54px] w-full text-base"
          loading={isSubmitting}
        >
          Link anfordern
        </Button>
        <Link to="/login" className={`${authBackLinkClass} self-center`}>
          Zurück zur Anmeldung
        </Link>
      </form>
    </>
  );
}
